import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "../ui/button";
import { 
  Search, Filter, KanbanSquare, List as ListIcon, 
  ChevronDown, Archive, FileText, Settings, Plus
} from "lucide-react";
import { cn } from "../ui/utils";
import { Ticket } from "./TicketCard";
import { TicketKanbanView } from "./TicketKanbanView";
import { TicketListView } from "./TicketListView";
import { TicketFormsView } from "./TicketFormsView";
import { TicketFilterPopover } from "./TicketFilterPopover";
import { Badge } from "../ui/badge";
import { ServiceDeskTeamSettings } from "./ServiceDeskTeamSettings";
import { toast } from "sonner";
import api from "../../utils/api";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ResolutionDetailsModal } from "./ResolutionDetailsModal";

import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";

interface GenericServiceDeskPageProps {
  id: string;
  title: string;
  description: string;
  initialTickets: Ticket[];
  forms: any[];
  statsLabel: string;
  searchPlaceholder: string;
  isRetired: boolean;
  hideTabs?: string[];
  canSubmit?: boolean;
}

const STATUS_CONFIG = {
  NEW: { label: "New Request", color: "bg-blue-50 text-blue-700 border-blue-100", kanbanColor: "bg-gray-50" },
  TODO: { label: "To Do", color: "bg-gray-100 text-gray-700 border-gray-200", kanbanColor: "bg-blue-50/50 border-blue-100" },
  DOING: { label: "Doing", color: "bg-orange-50 text-orange-700 border-orange-200", kanbanColor: "bg-orange-50/50 border-orange-100" },
  BLOCKED: { label: "Blocked", color: "bg-red-50 text-red-700 border-red-200", kanbanColor: "bg-red-50/50 border-red-100" },
  RESOLVED: { label: "Resolved", color: "bg-green-50 text-green-700 border-green-200", kanbanColor: "bg-green-50/50 border-green-100" },
  CLOSED: { label: "Closed", color: "bg-gray-100 text-gray-500 border-gray-200", kanbanColor: "bg-gray-100" },
};

export const GenericServiceDeskPage: React.FC<GenericServiceDeskPageProps> = ({
  id,
  title,
  description,
  initialTickets,
  forms,
  statsLabel,
  searchPlaceholder,
  isRetired,
  hideTabs = [],
  canSubmit = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ability = React.useContext(AbilityContext);
  const [activeTab, setActiveTab] = useState<'kanban' | 'list' | 'forms' | 'settings'>(
    hideTabs.includes('kanban') ? 'list' : 'kanban'
  );

  const canMove = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_AGENT) || 
                  ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN);

  const [tickets, setTickets] = useState(initialTickets);
  const [selectedServiceDesk, setSelectedServiceDesk] = useState<string>("all");
  const [selectedAssignee, setSelectedAssignee] = useState<string>(queryParams.get('assignee') || "all");
  const [showArchived, setShowArchived] = useState(false);
  const [isResolutionModalOpen, setIsResolutionModalOpen] = useState(false);
  const [resolvingTicket, setResolvingTicket] = useState<Ticket | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const serviceDesks = [
    "HR Service Desk",
    "IT Service Desk",
    "Facilities Desk",
    "Operations Support"
  ];

  const assignees = useMemo(() => {
    const names = new Set(initialTickets.flatMap(t => t.assignees?.map(u => u.name) || []).filter(Boolean));
    return Array.from(names).sort() as string[];
  }, [initialTickets]);

  const filteredTickets = tickets.filter(ticket => {
    const deskMatch = selectedServiceDesk === "all" || ticket.serviceDesk === selectedServiceDesk;
    let assigneeMatch = selectedAssignee === "all";
    if (!assigneeMatch) {
      if (selectedAssignee === "unassigned") {
        assigneeMatch = !ticket.assignees || ticket.assignees.length === 0;
      } else {
        assigneeMatch = ticket.assignees?.some(u => u.name === selectedAssignee) || false;
      }
    }
    return deskMatch && assigneeMatch;
  });

  const handleTicketMove = async (ticketId: string, newStatus: string) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket || ticket.status === newStatus) return;

    if (newStatus === 'RESOLVED') {
      setResolvingTicket(ticket);
      setIsResolutionModalOpen(true);
      return;
    }

    // Capture original status for rollback
    const originalStatus = ticket.status;

    // Optimistically update UI
    setTickets(prev => prev.map(t => 
      t.id === ticketId ? { ...t, status: newStatus } : t
    ));

    try {
      const response = await api.patch(`/api/service-desk/requests/${ticketId}/status`, { status: newStatus });
      if (!response.success) {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
      toast.error("Failed to update ticket status. Reverting change.");
      
      // Rollback to original status
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, status: originalStatus } : t
      ));
    }
  };

  const handleResolutionSubmit = async (details: any) => {
    if (!resolvingTicket) return;
    
    setIsUpdatingStatus(true);
    try {
      const response = await api.patch(`/api/service-desk/requests/${resolvingTicket.id}/status`, { 
        status: 'RESOLVED',
        resolutionDetails: details
      });

      if (response.success) {
        setTickets(prev => prev.map(t => 
          t.id === resolvingTicket.id ? { ...t, status: 'RESOLVED' } : t
        ));
        setIsResolutionModalOpen(false);
        setResolvingTicket(null);
        toast.success("Ticket resolved successfully");
      }
    } catch (error) {
      console.error("Error resolving ticket:", error);
      toast.error("Failed to resolve ticket");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <div className="space-y-6 pb-10 md:pb-0">
      {/* Read-Only Banner for Retired Desks */}
      {isRetired && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 shadow-sm">
          <Archive className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm tracking-tight">
            <span className="font-bold text-amber-900">Read-Only View:</span> This service desk is retired. All data is frozen for historical purposes.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-4 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold">{title}</h1>
              {isRetired && (
                <Badge variant="secondary" className="bg-gray-100 text-gray-500 border-gray-200 font-bold uppercase text-[10px] px-2 py-0.5 rounded shadow-sm">
                  Retired
                </Badge>
              )}
            </div>
            <p className="hidden sm:block text-sm text-muted-foreground">{description}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Desktop Filter */}
            <div className="hidden sm:block">
              <TicketFilterPopover 
                serviceDesks={serviceDesks}
                assignees={assignees}
                selectedServiceDesk={selectedServiceDesk}
                setSelectedServiceDesk={setSelectedServiceDesk}
                selectedAssignee={selectedAssignee}
                setSelectedAssignee={setSelectedAssignee}
                hideDeskFilter={true}
              />
            </div>

            {/* Desktop Tab Selector */}
            <div className="hidden sm:flex items-center bg-gray-100/50 p-1 rounded-lg border border-gray-100">
              {!hideTabs.includes('kanban') && (
                <button 
                  onClick={() => setActiveTab('kanban')}
                  className={cn(
                    "flex items-center px-3 py-1 rounded-md text-sm font-bold transition-all",
                    activeTab === 'kanban' ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <KanbanSquare className="mr-2 h-4 w-4" /> Kanban
                </button>
              )}
              {!hideTabs.includes('list') && (
                <button 
                  onClick={() => setActiveTab('list')}
                  className={cn(
                    "flex items-center px-3 py-1 rounded-md text-sm font-bold transition-all",
                    activeTab === 'list' ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <ListIcon className="mr-2 h-4 w-4" /> List
                </button>
              )}
              {!hideTabs.includes('forms') && (ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN)) && (
                <button 
                  onClick={() => setActiveTab('forms')}
                  className={cn(
                    "flex items-center px-3 py-1 rounded-md text-sm font-bold transition-all",
                    activeTab === 'forms' ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <FileText className="mr-2 h-4 w-4" /> Forms
                </button>
              )}
              {!hideTabs.includes('settings') && !isRetired && (ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN)) && (
                <button 
                  onClick={() => setActiveTab('settings')}
                  className={cn(
                    "flex items-center px-3 py-1 rounded-md text-sm font-bold transition-all",
                    activeTab === 'settings' ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </button>
              )}
            </div>

            {canSubmit && (
              <>
                {/* Desktop New Request */}
                <Button 
                    className="hidden sm:flex h-9 font-bold shadow-sm"
                    onClick={() => navigate(`/service-desk/${id}/requests/new`, { state: { from: `/service-desk/${id}` } })}
                    disabled={isRetired}
                >
                    <Plus className="mr-2 h-4 w-4" /> New Request
                </Button>
                
                {/* Mobile New Request Icon */}
                <Button 
                  size="icon"
                  className="sm:hidden h-10 w-10 shadow-md"
                  onClick={() => navigate(`/service-desk/${id}/requests/new`, { state: { from: `/service-desk/${id}` } })}
                  disabled={isRetired}
                >
                    <Plus className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Subtitle */}
        <p className="sm:hidden text-sm text-muted-foreground mt-1">{description}</p>

        {/* Mobile Filter Row - Directly on top of view selector */}
        <div className="sm:hidden">
          <TicketFilterPopover 
            serviceDesks={serviceDesks}
            assignees={assignees}
            selectedServiceDesk={selectedServiceDesk}
            setSelectedServiceDesk={setSelectedServiceDesk}
            selectedAssignee={selectedAssignee}
            setSelectedAssignee={setSelectedAssignee}
            hideDeskFilter={true}
            className="w-full h-11"
          />
        </div>
      </div>

      {/* Mobile Tab Selector */}
      <div className="sm:hidden w-full mb-6">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-200 h-11 rounded-xl">
              <div className="flex items-center gap-2">
                {activeTab === 'kanban' && <KanbanSquare size={18} className="text-slate-500" />}
                {activeTab === 'list' && <ListIcon size={18} className="text-slate-500" />}
                {activeTab === 'forms' && <FileText size={18} className="text-slate-500" />}
                {activeTab === 'settings' && <Settings size={18} className="text-slate-500" />}
                <span className="font-medium text-gray-900 capitalize">
                  {activeTab}
                </span>
              </div>
              <ChevronDown size={18} className="text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] p-2 space-y-1">
            {!hideTabs.includes('kanban') && (
              <DropdownMenuItem onClick={() => setActiveTab('kanban')} className="rounded-lg h-11 font-medium gap-3">
                <KanbanSquare size={18} className="text-blue-600" /> Kanban
              </DropdownMenuItem>
            )}
            {!hideTabs.includes('list') && (
              <DropdownMenuItem onClick={() => setActiveTab('list')} className="rounded-lg h-11 font-medium gap-3">
                <ListIcon size={18} className="text-blue-600" /> List
              </DropdownMenuItem>
            )}
            {!hideTabs.includes('forms') && (ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN)) && (
              <DropdownMenuItem onClick={() => setActiveTab('forms')} className="rounded-lg h-11 font-medium gap-3">
                <FileText size={18} className="text-blue-600" /> Forms
              </DropdownMenuItem>
            )}
            {!hideTabs.includes('settings') && !isRetired && (ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN)) && (
              <DropdownMenuItem onClick={() => setActiveTab('settings')} className="rounded-lg h-11 font-medium gap-3">
                <Settings size={18} className="text-blue-600" /> Settings
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats - Grid on mobile, Flex on desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
        <div className="bg-gray-100/50 rounded-lg p-3 border border-gray-100 shadow-sm sm:w-fit">
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">{statsLabel}</p>
          <p className="text-2xl font-bold mt-0.5 text-gray-900">{tickets.length}</p>
        </div>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className={cn("rounded-lg p-3 border shadow-sm sm:w-fit", config.color)}>
            <p className={cn("text-[10px] font-bold uppercase tracking-tight opacity-70")}>{config.label}</p>
            <p className={cn("text-2xl font-bold mt-0.5")}>{tickets.filter(t => t.status === key).length}</p>
          </div>
        ))}
      </div>

      {/* View Content */}
      {activeTab === 'list' && (
        <TicketListView 
          tickets={filteredTickets} 
          statusConfig={STATUS_CONFIG} 
          onTicketClick={(ticket) => navigate(`/service-desk/view/${ticket.id}`)}
        />
      )}
      
      {activeTab === 'kanban' && (
        <TicketKanbanView 
          tickets={filteredTickets} 
          statusConfig={STATUS_CONFIG} 
          onTicketMove={handleTicketMove}
          onTicketClick={(ticket) => navigate(`/service-desk/view/${ticket.id}`)}
          canMove={canMove}
        />
      )}

      {activeTab === 'forms' && (
        <TicketFormsView 
          deskTitle={title}
          forms={forms}
          onEditForm={(formId) => navigate(`/service-desk/${id}/forms/${formId}`)}
          onPreviewForm={(formId) => navigate(`/service-desk/${id}/forms/${formId}/preview`)}
        />
      )}

      {activeTab === 'settings' && (
        <ServiceDeskTeamSettings deskTitle={title} deskId={id} />
      )}

      <ResolutionDetailsModal 
        isOpen={isResolutionModalOpen}
        onOpenChange={setIsResolutionModalOpen}
        onSubmit={handleResolutionSubmit}
        requesterName={resolvingTicket?.submitter?.name}
        isSubmitting={isUpdatingStatus}
      />
    </div>
  );
};
